import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, X, FileText, ClipboardList, Box, DoorOpen, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import api from '../lib/api';

export default function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    if (query.length < 2) {
      setResults(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/search', { params: { q: query, limit: 5 } });
        setResults(data);
      } catch (err) {
        console.error('Search error:', err);
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && query.length >= 2) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setIsOpen(false);
      setQuery('');
    }
  };

  const navigateTo = (path) => {
    navigate(path);
    setIsOpen(false);
    setQuery('');
  };

  const totalResults = results?.total_count || 0;

  return (
    <div ref={containerRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('search.placeholder')}
          className="ps-9 pe-9 bg-secondary/50 border-border focus:bg-card"
          data-testid="global-search-input"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults(null);
            }}
            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown Results */}
      {isOpen && query.length >= 2 && (
        <div className="absolute top-full mt-2 w-full bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden" data-testid="search-dropdown">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : totalResults === 0 ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground text-sm">{t('search.noResults')}</p>
              <div className="mt-4 pt-4 border-t border-border/50">
                <p className="text-xs text-muted-foreground/70 mb-2">{t('shortcuts.search')} (Ctrl+K)</p>
                <div className="flex flex-wrap justify-center gap-3 text-xs text-muted-foreground/60">
                  <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">n</kbd> {t('shortcuts.newWorkRequest')}</span>
                  <span><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px]">⇧N</kbd> {t('shortcuts.newWorkOrder')}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {/* Work Orders */}
              {results?.work_orders?.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50 flex items-center gap-2">
                    <ClipboardList className="w-3.5 h-3.5" />
                    {t('search.workOrders')} ({results.work_orders.length})
                  </div>
                  {results.work_orders.map((wo) => (
                    <button
                      key={wo.id}
                      onClick={() => navigateTo(`/work-orders/${wo.id}`)}
                      className="w-full px-3 py-2 text-start hover:bg-muted/50 flex items-center gap-3 transition-colors"
                    >
                      <span className="font-mono text-xs text-muted-foreground">{wo.id.slice(0, 8)}</span>
                      <span className="text-sm truncate flex-1">{wo.branch_name}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-secondary text-muted-foreground">{wo.status}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Work Requests */}
              {results?.work_requests?.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50 flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" />
                    {t('search.workRequests')} ({results.work_requests.length})
                  </div>
                  {results.work_requests.map((wr) => (
                    <button
                      key={wr.id}
                      onClick={() => navigateTo(`/work-requests/${wr.id}`)}
                      className="w-full px-3 py-2 text-start hover:bg-muted/50 flex items-center gap-3 transition-colors"
                    >
                      <span className="font-mono text-xs text-muted-foreground">{wr.id.slice(0, 8)}</span>
                      <span className="text-sm truncate flex-1">{wr.title}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-secondary text-muted-foreground">{wr.status}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Assets */}
              {results?.assets?.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50 flex items-center gap-2">
                    <Box className="w-3.5 h-3.5" />
                    {t('search.assets')} ({results.assets.length})
                  </div>
                  {results.assets.map((asset) => (
                    <button
                      key={asset.id}
                      onClick={() => navigateTo(`/rooms?tab=assets&highlight=${asset.id}`)}
                      className="w-full px-3 py-2 text-start hover:bg-muted/50 flex items-center gap-3 transition-colors"
                    >
                      <span className="font-mono text-xs text-muted-foreground">{asset.serial || asset.id.slice(0, 8)}</span>
                      <span className="text-sm truncate flex-1">{asset.name}</span>
                      <span className="text-xs text-muted-foreground">{asset.branch_name}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Rooms */}
              {results?.rooms?.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50 flex items-center gap-2">
                    <DoorOpen className="w-3.5 h-3.5" />
                    {t('search.rooms')} ({results.rooms.length})
                  </div>
                  {results.rooms.map((room) => (
                    <button
                      key={room.id}
                      onClick={() => navigateTo(`/rooms?tab=rooms&highlight=${room.id}`)}
                      className="w-full px-3 py-2 text-start hover:bg-muted/50 flex items-center gap-3 transition-colors"
                    >
                      <span className="font-mono text-xs text-muted-foreground">{room.code || room.id.slice(0, 8)}</span>
                      <span className="text-sm truncate flex-1">{room.name}</span>
                      <span className="text-xs text-muted-foreground">{room.branch_name}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* View All */}
              <button
                onClick={() => {
                  navigate(`/search?q=${encodeURIComponent(query)}`);
                  setIsOpen(false);
                  setQuery('');
                }}
                className="w-full px-3 py-3 text-center text-sm text-brand hover:bg-muted/50 border-t border-border font-medium transition-colors"
              >
                {t('search.showAll')} ({totalResults})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
